import { useId, useRef, useState } from 'react'
import type { JSX, ReactNode } from 'react'

import { IconButton, NoteBlankIcon } from '@buffer-mono/popcorn'

import {
  DraftNotesThread,
  useContentItemNotesThread,
} from '~publish/components/ContentItemNotes'
import { SplitTagBadge } from '~publish/components/SplitTagBadge/SplitTagBadge'

import { COLLABORATION_NOTES_SPLIT } from '../PostDetailsContent/useCollaborationNotesPanel'

import styles from './ContentItemDetailsDialog.module.css'

/**
 * Lays the draft's details out beside its Notes panel, open by default as in
 * Post details. `children` renders the details and places the Notes toggle.
 *
 * The summary is read here, in its own query, only while the dialog is
 * open. Notes appear only once it answers: a null summary, a denial, or a
 * failed read hides them and leaves the draft as it is. Neither reading nor
 * adding a Note needs the draft's Edit permission; the summary's `canCreate`
 * decides the composer.
 */
export function DraftDetailsNotes({
  contentItemId,
  children,
}: {
  contentItemId: string
  children: (notesAction: ReactNode) => ReactNode
}): JSX.Element {
  const { thread, markClosed } = useContentItemNotesThread(contentItemId)
  const [open, setOpen] = useState(true)
  const panelRef = useRef<HTMLElement>(null)
  const panelId = useId()
  const headingId = useId()
  const available = thread.status === 'ready'

  return (
    <div className={styles.layout} data-notes-open={available && open}>
      <div className={styles.details}>
        {children(
          available ? (
            <IconButton
              label="Notes"
              tooltip="Notes"
              aria-controls={panelId}
              aria-expanded={open}
              aria-pressed={open}
              onClick={(): void => setOpen((current) => !current)}
              type="button"
              variant="secondary"
            >
              <NoteBlankIcon />
            </IconButton>
          ) : null,
        )}
      </div>
      {available && (
        <aside
          ref={panelRef}
          id={panelId}
          hidden={!open}
          tabIndex={-1}
          aria-labelledby={headingId}
          className={styles.notesPanel}
          data-testid="draft-details-notes"
        >
          <DraftNotesThread
            contentItemId={contentItemId}
            thread={thread}
            markClosed={markClosed}
            header={
              <header className={styles.notesHeader}>
                <h2 id={headingId} className={styles.notesTitle}>
                  Notes
                </h2>
                <SplitTagBadge splitName={COLLABORATION_NOTES_SPLIT} />
              </header>
            }
            fallbackFocusRef={panelRef}
          />
        </aside>
      )}
    </div>
  )
}
